import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { CellWithCount } from '../../common/react/components/dataGrid/CellWithCount';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { L10n } from '../../ts/L10n/L10n';
import { DeviceType } from '../../ts/models/DeviceType';
import {
  DataAlertRecipientInfo,
  DataAlertRecipientInfoType,
  IDataAlertInfo,
  UserDataAlertRecipientInfo,
} from '../../ts/models/IDataAlertInfo';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';

export namespace DataAlertsGrid {
  export interface DataAlertsGridScope extends CommonDataGridColumns.ISelectColumnScope<IDataAlertInfo> {}

  export function columns(opts: {
      translate: IReactTranslateService,
      scope: DataAlertsGridScope,
      actions: IActionButton<IDataAlertInfo, {}>[],
      getActionTarget: () => IActionTarget<IDataAlertInfo, {}>,
      siteUrls: SiteUrls,
      server: ServerService,
      helpService: HelpService,
      onPillClick: (dataAlert: IDataAlertInfo) => void
  }): DataGrid.Column[] {

    const { translate, scope, actions, getActionTarget, siteUrls, server, helpService, onPillClick } = opts;

    const subjectColumn: DataGrid.Column = CommonDataGridColumns.withExtraSpace({
      name: 'subject-col',
      dataKey: 'title',
      direction: 'asc',
      headerText: translate('column_task_subject'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCell('name')
    });

    const subjectColumnWithActions = CommonDataGridColumns.withActions(subjectColumn, {
      actions,
      getActionTarget,
      translate
    }, scope);

    const ownerColumn = CommonDataGridColumns.owner(translate, siteUrls);
    ownerColumn.dataKey = 'ownerName';

    const viewNameColumn: DataGrid.Column = {
      name: 'view-name-col',
      dataKey: 'targetName',
      headerText: translate('column_view_header'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        const data = row.data as IDataAlertInfo;
        data.deviceType = data.deviceType.toString().toLowerCase() as DeviceType;

        return {
          text: getViewName(data),
          url: getViewUrl(data, siteUrls),
          iconClassName: 'tb-grid-icon-view',
          iconTitle: translate('view')
        };
      })
    };

    const numRecipientsShown = 2;
    const recipientsColumn: DataGrid.Column = {
      name: 'recipients-col',
      dataKey: 'displayRecipients',
      headerText: translate('column_recipients'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: RecipientsListCell(numRecipientsShown, translate, siteUrls, server, onPillClick, 'dda-recipients-cell'),
      sortable: false
    };

    const privacyColumn: DataGrid.Column = {
      name: 'privacy-col',
      dataKey: 'isPublic',
      headerText: translate('column_alertPrivacy'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc',
      cellFormatter: DataGridCells.PlainTextCellWithTransform('isPublic', (isPublic: boolean) => isPublic ? translate('dataAlertPublic') : translate('dataAlertPrivate')),
      sortable: false
    };

    const mapNotificationInterval = createMapNotificationInterval(translate);
    const notificationSentColumn: DataGrid.Column = {
      name: 'notification-frequency-col',
      dataKey: 'notificationIntervalInMinutes',
      headerText: translate('column_notificationFrequency'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc',
      cellFormatter: DataGridCells.PlainTextCellWithTransform('notificationIntervalInMinutes', mapNotificationInterval),
    };

    let lastCheckedColumn: DataGrid.Column = {
      name: 'last-checked-col',
      dataKey: 'lastChecked',
      headerText: translate('column_last_checked'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: buildLastCheckedFormatter(translate, helpService)
    };

    const lastAlertedColumn: DataGrid.Column = {
      name: 'last-alerted-col',
      dataKey: 'lastAlerted',
      headerText: translate('column_last_alerted'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: LastAlertCell(translate('never')),
      sortable: false
    };

    return _.compact([
      CommonDataGridColumns.selected(scope, (row: DataGrid.Row) => `dataalert-checkbox-${row.data.id}`),
      subjectColumnWithActions,
      viewNameColumn,
      ownerColumn,
      recipientsColumn,
      FeatureFlags.displayDataAlertPrivacy ? privacyColumn : null,
      notificationSentColumn,
      lastCheckedColumn,
      lastAlertedColumn
    ]);
  }

  function buildLastCheckedFormatter(translate:IReactTranslateService, helpService:HelpService)
  {
    let cellFormatter: React.Factory<BodyCell.Props>;

    if (FeatureFlags.suspendDataAlerts) {
      cellFormatter = DataGridCells.InteractiveIconTooltipCell((row: DataGrid.Row) => {
        const alertInfo = row.data as IDataAlertInfo;
        const dateTime = row.data.lastChecked;
        const dateAsText = dateTime ? L10n.localizedDateTime(dateTime) : translate('never');

        return {
          text: dateAsText,
          title: Messages.learnMore(),
          iconClass:(alertInfo.suspendState !== 'NotSuspended') ? 'tb-react-dg-hcol tb-icon-server-alert-list-suspended' : null,
          tooltipText:'suspendedAlertTooltipText',
          tooltipLinkText: translate('learnMore'),
          tooltipLink: helpService.getServerHelpUrlByCategory('alert-suspend')
        };
      }, translate);
    } else {
      cellFormatter = DataGridCells.DateTimeCell('lastChecked', translate('never'));
    }
    return cellFormatter;
  }
  function getViewName(data: IDataAlertInfo) : string {
    return data.context.isViewModified ? data.context.name + '*' : data.context.name;
  }

  function getViewUrl(data: IDataAlertInfo, siteUrls: SiteUrls) : string {
    if (data.context.isViewModified) {
      const finalSlashIndex = data.context.path.lastIndexOf('/');
      const penultimateSlashIndex = data.context.path.substring(0, finalSlashIndex).lastIndexOf('/');
      const originalViewPath = data.context.path.substring(0, penultimateSlashIndex);
      return siteUrls.viewWithDeviceTypeUrl(originalViewPath, data.deviceType);
    }
    else {
      return siteUrls.viewWithDeviceTypeUrl(data.context.path, data.deviceType);
    }
  }

  function createMapNotificationInterval(translate: IReactTranslateService){
    return function(notificationIntervalInMinutes: number){

      // let's be careful and check it isn't some invalid number
      if (notificationIntervalInMinutes < 0){
        return translate('unspecified');
      }
      const MAXINT = 2147483647;
      const HOUR = 60;
      switch (notificationIntervalInMinutes){
        case (0):
          return translate('frequencyAlways');
        case (HOUR):
          return translate('frequencyHourly');
        case(HOUR * 24):
          return translate('frequencyDaily');
        case(HOUR * 24 * 7):
          return translate('frequencyWeekly');
        case(MAXINT):
          return translate('frequencyOnce');
        default:
          return translate('frequencyXMinutes', {COUNT: notificationIntervalInMinutes}, L10n.Interpolations.Count);
      }
    };
  }

  function LastAlertCell(nullDateTimeLabel: string): React.StatelessComponent<BodyCell.Props> {
    return function LastAlertCell(props: BodyCell.Props) {
      const dataAlert = props.row.data as IDataAlertInfo;
      const lastAlerted: Date =  dataAlert.lastSentForCurrentUser;

      const text = lastAlerted ? L10n.localizedDateTime(lastAlerted) : nullDateTimeLabel;
      return <span data-tb-test-id={ 'last-alert-value' }>{ text }</span>;
    };
  }

  function RecipientsListCell(numRecipientsShown: number,
                              translate: IReactTranslateService,
                              siteUrls: SiteUrls,
                              server: ServerService,
                              onPillClick: (dataAlertInfo: IDataAlertInfo) => void,
                              testId?: string): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      private onClickFn = () => onPillClick(this.props.row.data as IDataAlertInfo);

      private textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis);

      public render() {
        const dataAlert = this.props.row.data as IDataAlertInfo;
        let recipients: DataAlertRecipientInfo[] = dataAlert.displayRecipients;

        if (!recipients || recipients.length === 0) {
          const noneText = translate('none');
          return <span>{ noneText }</span>;
        }

        const currentUser: UserDataAlertRecipientInfo = {
          type: 'user',
          user:  server.sessionUserInfo()
        };

        if (dataAlert.isCurrentUserSubscribed && !(isCurrentUser(dataAlert.displayRecipients[0], currentUser))) {
          recipients = recipients.filter(recipient => !isCurrentUser(recipient, currentUser));
          recipients = [currentUser as DataAlertRecipientInfo].concat(recipients);
        }

        recipients = recipients.slice(0, numRecipientsShown);

        const moreCount = dataAlert.recipientsCount - recipients.length || null;
        const moreText = moreCount > 0 && translate('moreRecipients', { COUNT: moreCount }, L10n.Interpolations.Count);

        const recipientElements = recipients.map((r, i) => {
          const text = getRecipientText(r);
          return (
            <span key={ getRecipientId(r) }>
              { i > 0 ? ', ' : null  }
              <a href={ getRecipientUrl(r) } title={ text }>{ text }</a>
            </span>
          );
        });
        return (
          <CellWithCount testId={`${testId}-${this.props.row.data.id}`} count={moreText} onCountClick={this.onClickFn}>
            <span style={ this.textStyles }>{ recipientElements }</span>
          </CellWithCount>
        );
      }
    });

    function isCurrentUser(recipient: DataAlertRecipientInfo, currentUser: UserDataAlertRecipientInfo): boolean {
      return (DataAlertRecipientInfoType.isUser(recipient)
        && recipient.user.id === currentUser.user.id);
    }

    function getRecipientText(recipient: DataAlertRecipientInfo): string {
      let text = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        text = recipient.user.displayName;
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        text = recipient.group.name;
      }
      return text;
    }

    function getRecipientUrl(recipient: DataAlertRecipientInfo): string {
      let url = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        url = siteUrls.userContentUrl(recipient.user.username, recipient.user.domainName);
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        url = siteUrls.groupUrl(recipient.group.id);
      }
      return url;
    }

    function getRecipientId(recipient: DataAlertRecipientInfo): string {
      let id = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        id = recipient.user.id;
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        id = recipient.group.id;
      }
      return id;
    }
  }
}
